export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  body: string;
}

interface ParsedPost {
  data: {
    title: string;
    date: string;
    excerpt: string;
    slug: string;
  };
  content: string;
}

function parseFrontmatter(raw: string): ParsedPost {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match || match[1] === undefined || match[2] === undefined) {
    throw new Error('Invalid frontmatter');
  }
  const frontmatterRaw = match[1];
  const content = match[2];
  const data: Record<string, string> = {};
  for (const line of frontmatterRaw.split('\n')) {
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const key = line.slice(0, colonIdx).trim();
    const value = line.slice(colonIdx + 1).trim().replace(/^"(.*)"$/, '$1');
    data[key] = value;
  }
  return {
    data: {
      title: data.title || '',
      date: data.date || '',
      excerpt: data.excerpt || '',
      slug: data.slug || '',
    },
    content: content.trim(),
  };
}

const rawFiles = import.meta.glob('/content/blog/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

function parseFile(path: string, raw: string): BlogPost {
  const { data, content } = parseFrontmatter(raw);
  if (!data.slug) {
    throw new Error(`Blog post ${path} is missing "slug" in frontmatter`);
  }
  return {
    slug: data.slug,
    title: data.title,
    date: data.date,
    excerpt: data.excerpt,
    body: content,
  };
}

const allPosts: BlogPost[] = Object.entries(rawFiles)
  .map(([path, raw]) => parseFile(path, raw))
  .sort((a, b) => b.date.localeCompare(a.date));

export function getAllPosts(): BlogPost[] {
  return allPosts;
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return allPosts.find((post) => post.slug === slug);
}

export function formatPostDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
